import React, { useEffect, useState } from "react";
import { Plus, Trash2, Quote, CalendarDays, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

export default function FrasesAdmin() {
  const res = adminApi.frases();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ texto: "", fecha: "", activo: true });

  const load = () => res.list().then(setItems).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    if (form.texto.trim().length < 5) { toast.error("Escribe una frase válida."); return; }
    try {
      await res.create({ texto: form.texto.trim(), fecha: form.fecha || "", activo: form.activo });
      toast.success("Frase agregada.");
      setOpen(false); setForm({ texto: "", fecha: "", activo: true });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggle = async (it) => {
    try { await res.update(it.id, { activo: !it.activo }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    try { await res.remove(id); toast.success("Eliminada."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const dated = items.filter((i) => i.fecha);
  const pool = items.filter((i) => !i.fecha);

  const Card = (it) => (
    <div key={it.id} data-testid={`frase-${it.id}`} className={`rounded-2xl ring-1 p-4 ${it.activo ? "bg-white ring-black/5" : "bg-[#F4F6F9] ring-black/5 opacity-60"}`}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-[#0A1C30] font-semibold leading-snug flex-1">“{it.texto}”</p>
        <button data-testid={`del-frase-${it.id}`} onClick={() => remove(it.id)} className="p-1.5 rounded-lg hover:bg-[#FDECEC] text-[#C0392B] flex-shrink-0"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="mt-2 flex items-center justify-between">
        {it.fecha ? <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#3B7DF6]"><CalendarDays className="w-3.5 h-3.5" /> {it.fecha}</span> : <span className="text-xs text-[#6B7688]">Rotación general</span>}
        <div className="flex items-center gap-2"><span className="text-xs text-[#6B7688]">{it.activo ? "Activa" : "Inactiva"}</span><Switch checked={it.activo} onCheckedChange={() => toggle(it)} /></div>
      </div>
    </div>
  );

  return (
    <div data-testid="frases-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0A1C30] flex items-center gap-2"><Quote className="w-6 h-6 text-[#E9A400]" /> Frases inspiradoras</h1>
          <p className="text-sm text-[#6B7688] mt-1">Prioridad: frase de la fecha de hoy → lista general que rota → IA (Gemini) → respaldo.</p>
        </div>
        <button data-testid="add-frase-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-xl bg-navy text-white font-bold px-4 py-2.5 text-sm hover:bg-[#0d2440] transition-colors">
          <Plus className="w-4 h-4" /> Nueva frase
        </button>
      </div>

      <div className="mt-6">
        <h2 className="text-sm font-extrabold tracking-wide text-[#B99B2E] flex items-center gap-2"><CalendarDays className="w-4 h-4" /> FECHAS ESPECIALES ({dated.length})</h2>
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {dated.map(Card)}
          {dated.length === 0 && <p className="text-sm text-[#6B7688]">No hay frases asignadas a una fecha.</p>}
        </div>
      </div>

      <div className="mt-8">
        <h2 className="text-sm font-extrabold tracking-wide text-[#B99B2E] flex items-center gap-2"><Sparkles className="w-4 h-4" /> LISTA GENERAL QUE ROTA ({pool.length})</h2>
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {pool.map(Card)}
          {pool.length === 0 && <p className="text-sm text-[#6B7688]">Sin frases en la lista general. Se usará la IA como respaldo.</p>}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nueva frase</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <div><Label className="mb-1.5 block font-bold">Frase</Label>
              <textarea data-testid="frase-texto-input" value={form.texto} onChange={(e) => setForm((f) => ({ ...f, texto: e.target.value }))} className="w-full min-h-[100px] rounded-xl border border-black/10 p-3 text-sm outline-none focus:ring-2 focus:ring-[var(--gold)]" placeholder="El liderazgo comienza con el ejemplo..." />
            </div>
            <div><Label className="mb-1.5 block font-bold">Fecha específica (opcional)</Label>
              <Input data-testid="frase-fecha-input" type="date" value={form.fecha} onChange={(e) => setForm((f) => ({ ...f, fecha: e.target.value }))} />
              <p className="mt-1 text-xs text-[#6B7688]">Déjala vacía para incluirla en la rotación general.</p>
            </div>
            <div className="flex items-center justify-between"><Label className="font-bold">Activa</Label><Switch checked={form.activo} onCheckedChange={(v) => setForm((f) => ({ ...f, activo: v }))} /></div>
          </div>
          <DialogFooter><button data-testid="save-frase-btn" onClick={create} className="rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-5 py-2.5">Guardar</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
